import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CalendarDateFormatter, CalendarEvent, CalendarEventAction, CalendarEventTimesChangedEvent, CalendarView, DAYS_OF_WEEK } from 'angular-calendar';

import * as moment from 'moment';
import { TourneeAgenda } from 'src/app/data/tournee/models/tournee-agenda.model';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject, forkJoin } from 'rxjs';
import { TourneesService } from 'src/app/data/tournee/services/tournees.service';
import { ModalService } from 'src/app/shared/services/modal/modal.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { SessionContext } from 'src/app/core/services/config/app.settings';
import { Animateur } from 'src/app/data/commission/models/animateur.model';
import { AnimateursService } from 'src/app/data/commission/services/animateurs/animateurs.service';
import { CustomDateFormatter } from '../../../commissions/pages/planning-commissions/CustomDateFormatter.provider';
import { LoaderService } from '../../../../../core/services/loader/loader.service';

const colors: any = {
    red: { primary: '#ad2121', secondary: '#FAE3E3' },
    blue: { primary: '#1e90ff', secondary: '#D1E8FF' },
    yellow: { primary: '#FFFB00', secondary: '#FFFEBF' },
    orange: { primary: '#FFA500', secondary: '#FFE5B5' }
};

moment.updateLocale('fr', {
    week: {
        dow: DAYS_OF_WEEK.MONDAY,
        doy: 0
    }
});

@Component({
    selector: 'app-planning-tournees',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './planning-tournees.component.html',
    styleUrls: ['./planning-tournees.component.scss'],
    providers: [{ provide: CalendarDateFormatter, useClass: CustomDateFormatter }],
    standalone: false
})
export class PlanningTourneesComponent implements OnInit {
    maxEventsMonthView = 3;
    view: CalendarView | 'year' = CalendarView.Week;
    CalendarView = CalendarView;
    viewDate: Date = new Date();
    refresh: Subject<void> = new Subject();
    events: CalendarEvent<{ tournee: TourneeAgenda }>[] = [];
    activeDayIsOpen = false;
    dateDebut: moment.Moment = moment().startOf('week');
    dateFin: moment.Moment = moment().endOf('week');
    locale = 'fr_FR';
    weekStartsOn: number = DAYS_OF_WEEK.MONDAY;
    weekendDays: number[] = [DAYS_OF_WEEK.SATURDAY, DAYS_OF_WEEK.SUNDAY];
    actions: CalendarEventAction[] = [
        {
            label: 'Editer  ',
            onClick: ({ event }: { event: CalendarEvent }): void => {
                this.handleEvent('Edited', event);
            }
        },
        {
            label: '  Supprimer',
            onClick: ({ event }: { event: CalendarEvent }): void => {
                this.handleEvent('Deleted', event);
            }
        }
    ];
    techniciens: Animateur[];
    formFiltre: UntypedFormGroup;

    get listeEvents() {
        const filtreAnimateur = this.formFiltre.get('technicien').value;
        if (filtreAnimateur === null) {
            return this.events;
        } else {
            return this.events.filter(evt => evt.meta.tournee.technicien.id === filtreAnimateur.id);
        }
    }

    get yearMonths(): Date[] {
        const year = this.viewDate.getFullYear();
        return Array.from({ length: 12 }, (_, index) => new Date(year, index, 1));
    }

    constructor(
        private readonly router: Router,
        private readonly tourneesService: TourneesService,
        private readonly toastr: ToastrService,
        private readonly translate: TranslateService,
        private readonly modalsService: ModalService,
        private readonly animateursService: AnimateursService,
        private readonly fb: UntypedFormBuilder,
        private readonly loaderService: LoaderService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.formFiltre = this.fb.group({
            technicien: [undefined]
        });
        this.formFiltre.get('technicien').valueChanges.subscribe(technicien => {
            this.refresh.next();
        });

        this.loadData();
    }

    loadData() {
        this.events = [];
        forkJoin(
            this.tourneesService.getAgendaTournees(SessionContext.get('idOrganisme'), this.dateDebut, this.dateFin),
            this.animateursService.getAnimateurs(SessionContext.get('idOrganisme'))
        )
            .pipe(this.loaderService.operator())
            .subscribe(([tournees, animateurs]) => {
                this.techniciens = animateurs;
   
                this.events = (tournees ?? []).map((tournee: TourneeAgenda) => {
                                    let titre = `<b>${this.translate.instant('page.tournees.planning.numero')} : ${tournee.numero}</b><br />`;
                
                                    if (this.view === CalendarView.Month) {
                                        titre += `${this.translate.instant('page.tournees.planning.horaire')} : ${tournee.debut.format('HH:mm')} - ${tournee.fin.format('HH:mm')}<br />`;
                                    }
                
                                    titre += `- ${this.translate.instant('page.tournees.planning.technicien')} : ${tournee.technicien.prenom} ${tournee.technicien.nom}<br />- ${this.translate.instant('page.tournees.planning.echantillons')} : ${tournee.nbEchantillons}, ${this.translate.instant('page.tournees.planning.operateurs')} : ${tournee.nbOperateurs}`;
                
                                    return {
                                        title: titre,
                                        start: tournee.debut.toDate(),
                                        end: tournee.fin.toDate(),
                                        resizable: { beforeStart: false, afterEnd: false },
                                        draggable: false,
                                        color: tournee.terminee ? colors.orange : colors.yellow,
                                        actions: this.actions,
                                        meta: { tournee }
                                    } as CalendarEvent<{ tournee: TourneeAgenda }>;
                                });
                
                                console.log('events construits =', this.events.length);

                this.refresh.next();
                this.cdr.markForCheck();
            });
    }

    compareTechniciens(a: Animateur, b: Animateur) {
        return a && b ? a.id === b.id : a === b;
    }

    dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
        const dateJour = moment([date.getFullYear(), date.getMonth(), date.getDate()]);
        const dateVue = moment([this.viewDate.getFullYear(), this.viewDate.getMonth(), this.viewDate.getDate()]);
        if (dateVue.isSame(dateJour, 'month')) {
            if ((dateVue.isSame(dateJour, 'day') && this.activeDayIsOpen === true) || events.length === 0) {
                this.activeDayIsOpen = false;
            } else {
                this.activeDayIsOpen = true;
            }
            this.viewDate = date;
        }
    }

    eventTimesChanged({ event, newStart, newEnd }: CalendarEventTimesChangedEvent): void {
        this.events = this.events.map(iEvent => {
            if (iEvent === event) {
                return {
                    ...event,
                    start: newStart,
                    end: newEnd
                };
            }
            return iEvent;
        });
        this.handleEvent('Dropped or resized', event);
    }

    onHourSegmentClicked(event: { date: Date; sourceEvent?: MouseEvent }): void {
            const date = event.date;
    
            this.router.navigate(['main/controles/tournees/new/edit'], {
                queryParams: {
                     debut: moment(event.date).format('YYYY-MM-DDTHH:mm:ss')
                }
            });
        }

    handleEvent(action: string, event: CalendarEvent): void {
        if (action === 'Clicked' || action === 'Edited') {
            this.router.navigate([`main/controles/tournees/${event.meta.tournee.id}/edit`]);
        } else if (action === 'Deleted') {
            if (
                event.meta.tournee.nbEchantillons > 0 ||
                event.meta.tournee.nbJuresTotal > 0 ||
                event.meta.tournee.nbJuresInscrits > 0 ||
                event.meta.tournee.nbJuresPresents > 0
            ) {
                this.modalsService.alert(this.translate.instant('page.tournees.planning.removeDisable'));
            } else {
                this.modalsService
                    .confirm(
                        `${this.translate.instant('page.tournees.planning.confirm_remove.message')} ${event.meta.tournee.numero} ?`,
                        this.translate.instant('page.tournees.planning.confirm_remove.title'),
                        { cancelText: this.translate.instant('label.annuler'), submitText: this.translate.instant('label.valider') }
                    )
                    .then(() => {
                        this.tourneesService.deleteTournee(SessionContext.get('idOrganisme'), event.meta.tournee).subscribe(() => {
                            this.deleteEvent(event);
                            this.refresh.next();
                            this.toastr.success(
                                this.translate.instant('page.tournees.edit.alert.suppression.message', { numero: event.meta.tournee.numero }),
                                this.translate.instant('page.tournees.edit.alert.suppression.title'),
                                { timeOut: 10 * 1000 }
                            );
                        });
                    });
            }
        }
    }

    deleteEvent(eventToDelete: CalendarEvent) {
        this.events = this.events.filter(event => event !== eventToDelete);
    }

    setView(view: CalendarView | 'year') {
        this.view = view;
        this.refreshData();
    }

    openMonth(date: Date): void {
        this.viewDate = new Date(date);
        this.view = CalendarView.Month;
        this.activeDayIsOpen = false;
        this.refreshDatesIntervalle();
        this.loadData();
    }

    goToToday(): void {
            this.viewDate = new Date();
            this.activeDayIsOpen = false;
            this.refreshDatesIntervalle();
            this.loadData();
    }

    previousPeriod(): void {
        const newDate = new Date(this.viewDate);

        if (this.view === 'year') {
            newDate.setFullYear(newDate.getFullYear() - 1);
        } else if (this.view === CalendarView.Month) {
            newDate.setMonth(newDate.getMonth() - 1);
        } else if (this.view === CalendarView.Week) {
            newDate.setDate(newDate.getDate() - 7);
        } else {
            newDate.setDate(newDate.getDate() - 1);
        }

        this.viewDate = newDate;
        this.activeDayIsOpen = false;
        this.refreshDatesIntervalle();
        this.loadData();
    }

    nextPeriod(): void {
        const newDate = new Date(this.viewDate);

        if (this.view === 'year') {
            newDate.setFullYear(newDate.getFullYear() + 1);
        } else if (this.view === CalendarView.Month) {
            newDate.setMonth(newDate.getMonth() + 1);
        } else if (this.view === CalendarView.Week) {
            newDate.setDate(newDate.getDate() + 7);
        } else {
            newDate.setDate(newDate.getDate() + 1);
        }

        this.viewDate = newDate;
        this.activeDayIsOpen = false;
        this.refreshDatesIntervalle();
        this.loadData();
    }

    closeOpenMonthViewDay() {
        this.activeDayIsOpen = false;
        this.refreshData();
    }

    refreshDatesIntervalle() {
        switch (this.view) {
            case 'year':
                this.dateDebut = moment([this.viewDate.getFullYear(), 0, 1]).startOf('year');
                this.dateFin = moment([this.viewDate.getFullYear(), 0, 1]).endOf('year');
                break;

            case CalendarView.Month:
                this.dateDebut = moment([this.viewDate.getFullYear(), this.viewDate.getMonth(), this.viewDate.getDate()]).startOf('month');
                this.dateFin = moment([this.viewDate.getFullYear(), this.viewDate.getMonth(), this.viewDate.getDate()]).endOf('month');
                break;
            case CalendarView.Week:
                this.dateDebut = moment([this.viewDate.getFullYear(), this.viewDate.getMonth(), this.viewDate.getDate()]).startOf('week');
                this.dateFin = moment([this.viewDate.getFullYear(), this.viewDate.getMonth(), this.viewDate.getDate()]).endOf('week');
                break;
            default:
                this.dateDebut = moment([this.viewDate.getFullYear(), this.viewDate.getMonth(), this.viewDate.getDate()]);
                this.dateFin = moment([this.viewDate.getFullYear(), this.viewDate.getMonth(), this.viewDate.getDate()]);
        }
    }

    refreshData() {
        this.refreshDatesIntervalle();
        this.loadData();
    }

    getEventsForMonth(monthDate: Date): CalendarEvent<{ tournee: TourneeAgenda }>[] {
            const month = monthDate.getMonth();
            const year = monthDate.getFullYear();
    
            return this.listeEvents
                .filter(event => {
                    const eventDate = new Date(event.start);
                    return eventDate.getFullYear() === year && eventDate.getMonth() === month;
                })
                .sort((a, b) => a.start.getTime() - b.start.getTime());
    }

    get yearMonthsWithEvents() {
        const year = this.viewDate.getFullYear();

        return Array.from({ length: 12 }, (_, index) => {
            const date = new Date(year, index, 1);

            const events = this.listeEvents
                .filter(event => {
                    const eventDate = new Date(event.start);
                    return eventDate.getFullYear() === year && eventDate.getMonth() === index;
                })
                .sort((a, b) => a.start.getTime() - b.start.getTime());

            return { date, events };
        });
    }
}
